import React, { useCallback, useEffect, useRef, useState } from 'react'
import Carousel from 'react-multi-carousel'
import ProgramCard from '../components/ProgramCard'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { fetchProgramById, fetchPrograms } from '../api/programmation'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import ProgramPlaceholder from '../components/ProgramPlaceholder'
import useEmblaCarousel from 'embla-carousel-react'


const Grille = () => {

    const responsiveDate = {
        desktop1: {
            breakpoint: { max: 6000, min: 1200 },
            items: 7,
        },
        desktop: {
            breakpoint: { max: 1200, min: 992 },
            items: 5,
        },
        tablet1: {
            breakpoint: { max: 992, min: 768 },
            items: 5,
        },
        tablet: {
            breakpoint: { max: 768, min: 576 },
            items: 4,
        },
        mobile: {
            breakpoint: { max: 576, min: 0 },
            items: 3,
        }
    }

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, duration: 0 });
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const updateButtons = useCallback(() => {
        if (!emblaApi) return;
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        updateButtons();
        emblaApi.on('select', updateButtons);
        emblaApi.on('reInit', updateButtons);
    }, [emblaApi, updateButtons]);





    const today = new Date().toISOString().split("T")[0];

    const [programId, setProgramId]= useState(null)

    const { data: singleProgram, isLoading: isFetchingSingle } = useQuery({
        queryKey: ['programU', programId],
        queryFn: () => fetchProgramById(programId),
        enabled: !!programId,
    });




    const { data: programsList, fetchNextPage, hasNextPage, isFetchingNextPage, status: listStatus } = useInfiniteQuery({
        queryKey: ["program"],
        queryFn: fetchPrograms,
        getNextPageParam: (lastPage) => lastPage.nextPage,
        retry: 2,
    });

    const observer = useRef();
    const lastMediaRef = useCallback((node) => {
        if (isFetchingNextPage) return;
        if (!hasNextPage) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                fetchNextPage(); 
            }
        });

        if (node) observer.current.observe(node);
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);




    // Filtrer, trier et définir programId sur le premier élément
    useEffect(() => {
        if (programsList?.pages?.length > 0) {
            // 1. Filtrer les dates passées
            const filteredPrograms = programsList.pages
                .flatMap((page) => page.data)
                .filter((item) => item.dayAt >= today);

            // 2. Trier les dates par ordre chronologique
            const sortedPrograms = filteredPrograms.sort((a, b) =>
                a.dayAt.localeCompare(b.dayAt)
            );

            // 3. Prendre le premier élément
            const firstProgram = sortedPrograms[0];

            // 4. Définir programId sur l'ID du premier élément
            if (firstProgram) {
                setProgramId(firstProgram.id);
            }
        }
    }, [programsList, today]);

    return (
        <div className='text-white mb-4 container-xxl'>
            {/* <h1 className='fs-3 fw-bold'>Grille TV</h1> */}


            {/* Dates */}
            <div className=' my-2 bgcolor-footer px-3 position-sticky z-3' style={{top: "65px"}}>
                {listStatus === "pending" ?
                    <Carousel responsive={responsiveDate} infinite={false} className='py-2'>
                    {
                        [1,2,3,4,5,6,7].map((day, index) => (
                            <div className='mx-sm-2 mx-1 placeholder-wave' key={index}>
                                <div role='button' className={`px-2 py-1 grille-date ${day === 1 ? "bgcolor-primary" : ""}`} style={{width:'auto'}}>
                                    <div className=' d-flex flex-column justify-content-center align-items-center'>
                                        <p className='fs-9s placeholder bg-white col-6 mb-1 lh-sm'></p>
                                        <p className='fs-8s mb-0 lh-sm placeholder bg-white col-10'></p>
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                    </Carousel>
                : listStatus === "error" ? 
                    <p className='text-danger py-4'>Ooops ! Une erreur c'est produite</p>
                :programsList?.pages?.flatMap((page, pageIndex) => 
                    ((programsList?.pages?.length <= 1) && !page?.nextPage) ?
                        <h3 className='fs-4 fw-bold mb-0 py-2'>Aucune date disponible</h3>
                    :((page?.data
                    ?.filter(item => item.dayAt >= today)
                    ?.length <= 0) && (page.nextPage)) ?
                        <p className='text-info mb-0 py-3'>Aucune date disponible pour les jours à venir.</p>
                    :
                    <Carousel responsive={responsiveDate} infinite={false}>
                    {
                        page?.data
                        ?.filter(item => item.dayAt >= today) 
                        ?.sort((a, b) => a.dayAt.localeCompare(b.dayAt))
                        ?.slice(0, 7)
                        ?.map((program, index) => {
                            const isLastItem = pageIndex === programsList?.pages?.length - 1 && index === page?.data?.length - 1;
                            return (
                                <div className='mx-sm-2 mx-1 my-2' key={index} ref={isLastItem ? lastMediaRef : null}>
                                    <div role='button' onClick={()=>setProgramId(program?.id)} className={`px-2 py-1 grille-date ${programId === program?.id ? "bgcolor-primary" : ""}`} style={{width:'auto'}}>
                                        <div className=' d-flex flex-column justify-content-center align-items-center'>
                                            <p className='fs-9s fw-bold mb-0 lh-sm text-capitalize'>{format(new Date(program?.dayAt), "EEEE", {locale: fr})}</p>
                                            <p className='fs-8s mb-0 lh-sm text-capitalize'>{format(new Date(program?.dayAt), "dd MMMM", {locale: fr})}</p>
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    }
                    </Carousel>
                )}
            </div>


            {/* Programs */}
            <div className='my-2'>

            {
                !programId || isFetchingSingle?
                <div className=' placeholder-wave'>
                    {/* Web view */}
                    <div className="embla py-2 d-sm-flex d-none">
                        <div className="embla-viewport" ref={emblaRef}>
                            <div className="embla-container">
                            {
                                [1,2,3,4,5].map((num, index) => (
                                    <div key={index}>
                                        <ProgramPlaceholder />
                                    </div>
                                ))
                            }
                            </div>
                        </div>
                    </div>

                    {/* Mobile view */}
                    <div className='d-block d-sm-none'>
                        <div className=' d-flex flex-column'>
                            {
                                [1,2,3,4].map((num, index) => (
                                    <div key={index}>
                                        <ProgramPlaceholder />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
                :
                <>
                    {/* Web view */}
                    <div className="embla py-2 d-sm-flex d-none">
                        <div className="embla-viewport" ref={emblaRef}>
                            <div className="embla-container">
                            {
                                singleProgram?.data?.emissions
                                ?.sort((a, b) => a?.startAt?.localeCompare(b?.startAt)) // Classer par heure
                                ?.map((emission) => (
                                    <div className='embla-slide' key={emission?.id}>
                                        <ProgramCard image={emission?.picture} title={emission?.name} date={emission?.startAt?.slice(0,5)} />
                                    </div>
                                ))
                            }
                            </div>
                        </div>
                        <button className="embla-button embla-button--prev" onClick={() => emblaApi?.scrollPrev()} disabled={!canScrollPrev}><i className='text-black fa fa-angle-left'></i></button>
                        <button className="embla-button embla-button--next" onClick={() => emblaApi?.scrollNext()} disabled={!canScrollNext}><i className='text-black fa fa-angle-right'></i></button>
                    </div>

                    {/* Mobile view */}
                    <div className='d-block d-sm-none'>
                        <div className=' d-flex flex-column'>
                            {
                                singleProgram?.data?.emissions.map((emission, index) => (
                                    <div key={index}>
                                        <ProgramCard image={emission?.picture} title={emission?.name} date={emission?.startAt?.slice(0,5)} />
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </>
            }
            </div>
        </div>
    )
}

export default Grille